"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const ldLocalStorage_1 = require("./helpers/ldLocalStorage");
const prepareOutputPayload_1 = require("./helpers/prepareOutputPayload");
const saveIds_1 = require("./helpers/saveIds");
const storeFbIds_1 = require("./helpers/storeFbIds");
const storeGaIds_1 = require("./helpers/storeGaIds");
let ldPayload = {};
const ldLocalStorageExists = () => !!(0, ldLocalStorage_1.getLDLocalStorage)();
/**
 * Littledata fetchClientIds method for headless Shopify storefront setups.
 * Stores GA, FB and Segment client and session IDs to localStorage.
 * @param object Arguments are Google Analytics, Facebook, Segment tracking IDs. At least one of them must be provided.
 * @param {string | undefined} object.ga3PropertyId - Univeral Analytics web property ID
 * @param {string | undefined} object.ga4MeasurementId - GA4 Measurement ID
 * @param {string | undefined} object.fbPixelId - Facebook Pixel ID
 * @param {string | undefined} object.segmentWriteKey - Segment WriteKey
 * @param {string | undefined} object.tiktokPixelId - TikTok Pixel ID
 * @returns {Promise<IShopifyNoteAttribute[] | undefined>} Shopify Note Attributes for adding to cart or checkout
 */
const fetchClientIds = async ({ ga3PropertyId, ga4MeasurementId, fbPixelId, segmentWriteKey, tiktokPixelId, }) => {
    const trackingIds = {
        ga3PropertyId,
        ga4MeasurementId,
        fbPixelId,
        segmentWriteKey,
        tiktokPixelId,
    };
    const fbclidFromUrlIsNewer = (0, storeFbIds_1.isFbclidNewerInUrlThanAlreadyKnown)() === true;
    const gaSessionIdIsReset = typeof (await (0, storeGaIds_1.checkIfGaSessionIdNewer)(ga4MeasurementId)) === "string";
    if (!fbclidFromUrlIsNewer && !gaSessionIdIsReset && ldLocalStorageExists()) {
        ldPayload = { ...ldPayload, ...(0, ldLocalStorage_1.getLDLocalStorage)() };
        return (0, prepareOutputPayload_1.transformToNoteAttributes)(ldPayload);
    }
    await (0, saveIds_1.saveIdsToPayloadObj)(trackingIds, ldPayload);
    (0, ldLocalStorage_1.setLDLocalStorage)(ldPayload);
    return (0, prepareOutputPayload_1.transformToNoteAttributes)(ldPayload);
};
/**
 * Littledata's sendCartToLittledata method is used in cases where headless
 * storefront uses Shopify's Cart. It sends client IDs to the Littledata server
 * and outputs the Shopify Note Attributes payload,
 * ready to be sent just before the page does a redirect to Shopify Checkout.
 * @param {ISenderFunction} shopifyCartToken - cart token returned by Shopify
 * @returns {Promise<IShopifyNoteAttribute[] | undefined>} Shopify Note Attributes payload
 */
const sendCartToLittledata = async (shopifyCartToken) => {
    if (!shopifyCartToken || !ldLocalStorageExists()) {
        return;
    }
    (0, saveIds_1.saveTokensToPayloadObj)(ldPayload, shopifyCartToken);
    ldPayload = { ...ldPayload, ...(0, ldLocalStorage_1.getLDLocalStorage)() };
    await (0, prepareOutputPayload_1.xhrPostToLittledata)(ldPayload);
    return (0, prepareOutputPayload_1.transformToNoteAttributes)(ldPayload);
};
/**
 * Littledata's sendCheckoutToLittledata method is used in cases where headless
 * storefront uses Shopify's Checkout API. It sends saved clientIDs to the Littledata server.
 * Also, it outputs the Shopify Note Attributes payload as a Promise,
 * ready to be sent just before the merchant does a redirect to Shopify Checkout.
 * @param {ISenderFunction} shopifyCheckoutToken - Shopify checkout token
 * @returns {Promise<IShopifyNoteAttribute[] | undefined>} Outputs Shopify Note Attributes payload
 */
const sendCheckoutToLittledata = async (shopifyCheckoutToken) => {
    if (!shopifyCheckoutToken || !ldLocalStorageExists()) {
        return;
    }
    (0, saveIds_1.saveTokensToPayloadObj)(ldPayload, shopifyCheckoutToken);
    ldPayload = { ...ldPayload, ...(0, ldLocalStorage_1.getLDLocalStorage)() };
    await (0, prepareOutputPayload_1.xhrPostToLittledata)(ldPayload);
    return (0, prepareOutputPayload_1.transformToNoteAttributes)(ldPayload);
};
exports.default = {
    fetchClientIds,
    sendCartToLittledata,
    sendCheckoutToLittledata,
};
